import { Link } from "react-router-dom";
import { Instagram, Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";
import { SEEDH } from "@/lib/seedh";
import { CAUSE_LABEL } from "@/lib/causes";

const foundationLinks = [
    { label: "About", to: "/about" },
    { label: "How It Works", to: "/how-it-works" },
    { label: "Tracker", to: "/tracker" },
    { label: "Donate", to: "/donate" },
    { label: "Impact Gallery", to: "/gallery" },
    { label: "Contact Us", to: "/contact" },
];

const policyLinks = [
    { label: "Privacy Policy", to: "/privacy" },
    { label: "Terms & Conditions", to: "/terms" },
];

const focusLinks = [
    { label: "Tree Plantation", to: "/donate?cause=trees" },
    { label: "Food Drives", to: "/donate?cause=food" },
    { label: CAUSE_LABEL.education, to: "/donate?cause=education" },
];

export default function Footer() {
    return (
        <footer
            data-testid="site-footer"
            className="relative overflow-hidden bg-seedh-blue text-white"
        >
            <div
                aria-hidden
                className="absolute -top-px left-0 right-0 h-12"
                style={{
                    background:
                        "radial-gradient(120% 100% at 50% 0%, rgba(255,255,255,0.05) 0%, transparent 60%)",
                }}
            />
            <div className="max-w-7xl mx-auto px-5 md:px-10 pt-20 pb-10 relative">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-10">
                    <div className="md:col-span-5">
                        <img
                            src={SEEDH.logoLight}
                            alt={`${SEEDH.name} — ${SEEDH.tagline}`}
                            className="h-20 w-auto object-contain bg-white rounded-2xl p-3 border border-white/15"
                        />
                        <p className="mt-7 font-display text-xl text-white/90 leading-snug max-w-md">
                            Every contribution carries a story you can follow.
                        </p>
                        <p className="mt-5 text-sm text-white/65 max-w-md leading-relaxed">
                            {SEEDH.name} is an Udaipur-based welfare
                            foundation working across tree plantation, food
                            drives, and child welfare — with a transparent
                            public record of every contribution.
                        </p>

                        <a
                            href={SEEDH.instagram}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="footer-instagram"
                            className="inline-flex items-center gap-2 mt-7 text-sm group"
                        >
                            <span className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center transition-colors group-hover:bg-white/10">
                                <Instagram className="w-4 h-4" />
                            </span>
                            <span className="link-underline">
                                Follow on Instagram
                            </span>
                            <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                        </a>
                    </div>

                    <div className="md:col-span-2">
                        <div className="text-[0.7rem] tracking-[0.28em] uppercase text-white/55 mb-5">
                            Foundation
                        </div>
                        <ul className="space-y-3">
                            {foundationLinks.map((l) => (
                                <li key={l.label}>
                                    <Link
                                        to={l.to}
                                        className="text-sm text-white/85 link-underline hover:text-white"
                                    >
                                        {l.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="md:col-span-2">
                        <div className="text-[0.7rem] tracking-[0.28em] uppercase text-white/55 mb-5">
                            Focus areas
                        </div>
                        <ul className="space-y-3">
                            {focusLinks.map((l) => (
                                <li key={l.label}>
                                    <Link
                                        to={l.to}
                                        className="text-sm text-white/85 link-underline hover:text-white"
                                    >
                                        {l.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                        <div className="text-[0.7rem] tracking-[0.28em] uppercase text-white/55 mt-8 mb-4">
                            Legal
                        </div>
                        <ul className="space-y-2">
                            {policyLinks.map((l) => (
                                <li key={l.label}>
                                    <Link
                                        to={l.to}
                                        className="text-sm text-white/70 link-underline hover:text-white"
                                    >
                                        {l.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div id="contact" className="md:col-span-3">
                        <div className="text-[0.7rem] tracking-[0.28em] uppercase text-white/55 mb-5">
                            Get in touch
                        </div>
                        <ul className="space-y-4 text-sm">
                            <li className="flex items-start gap-3">
                                <Mail className="w-4 h-4 mt-0.5 text-seedh-green shrink-0" />
                                <a
                                    href={`mailto:${SEEDH.email}`}
                                    className="text-white/85 hover:text-white break-all"
                                >
                                    {SEEDH.email}
                                </a>
                            </li>
                            <li className="flex items-start gap-3">
                                <Phone className="w-4 h-4 mt-0.5 text-seedh-green shrink-0" />
                                <a
                                    href={`tel:${SEEDH.phoneDigits}`}
                                    className="text-white/85 hover:text-white"
                                >
                                    {SEEDH.phone}
                                </a>
                            </li>
                            <li className="flex items-start gap-3">
                                <MapPin className="w-4 h-4 mt-0.5 text-seedh-green shrink-0" />
                                <span className="text-white/85 leading-relaxed">
                                    {SEEDH.addressLines.map((l) => (
                                        <span key={l} className="block">
                                            {l}
                                        </span>
                                    ))}
                                </span>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="h-px w-full bg-white/10 mt-12 mb-6" />

                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 pb-8 text-xs text-white/55">
                    <div>© {new Date().getFullYear()} {SEEDH.name}. All rights reserved.</div>
                    <div className="flex items-center gap-1.5">
                        Built with care by
                        <span className="font-semibold tracking-wide text-white/80">ADHQ Solutions Pvt. Ltd.</span>
                    </div>
                </div>
            </div>
        </footer>
    );
}
